import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';

import './index.less';

import {
  IngestionSourceParametersOutput,
  IngestionSourceParametersType,
  WebsiteSourceParametersReceivingMode,
  WebsiteSourceParametersSettings,
} from '../../../../../../gamma-api';
import IbTypography from '../../../../../components/common/IbTypography';
import IbInput from '../../../../../components/common/IbInput';
import IbTextArea from '../../../../../components/common/IbTextArea';
import Switcher from '../../../../../components/common/Switcher';
import { FormAPI } from '../../../../../types';
import IbFormFieldLabel from '../../../../../components/common/IbFormFieldLabel';

import { MAIN_CLASS_NAME } from './const';
import { useValidationHandle } from './useValidationHandle';

const INCLUDED_PATHS_PLACEHOLDER = `/articles/*
/news/*`;

const EXCLUDED_PATHS_PLACEHOLDER = '/login; /settings/*; /about';

const getExtraUrlsIsValid = (receivingMode: WebsiteSourceParametersReceivingMode, extraUrls?: string[]) =>
  receivingMode !== WebsiteSourceParametersReceivingMode.Manual || extraUrls?.map((s) => s.trim()).join('\n').length;

export interface ISourceSettingsWebsiteProps {
  parameters: IngestionSourceParametersOutput;
  onChange?: (parameters: IngestionSourceParametersOutput) => void;
}

const SourceSettingsWebsite = forwardRef<FormAPI, ISourceSettingsWebsiteProps>(({ parameters, onChange }, ref) => {
  const { t } = useTranslation();
  const settings = parameters.settings as WebsiteSourceParametersSettings;

  const [errors, setErrors] = useValidationHandle(
    ref,
    (isTouched: boolean) => {
      return {
        name: { isTouched: isTouched, isError: !parameters.name },
        base_url: { isTouched: isTouched, isError: !settings.base_url },
        extra_urls: {
          isTouched: isTouched,
          isError: !getExtraUrlsIsValid(settings.receiving_mode, settings.extra_urls),
        },
      };
    },
    [parameters.name, settings.base_url, settings.receiving_mode, settings.extra_urls]
  );

  const validateExtraUrls = (receivingMode: WebsiteSourceParametersReceivingMode, extraUrls?: string[]) => {
    setErrors({
      ...errors,
      extra_urls: {
        isTouched: true,
        isError: !getExtraUrlsIsValid(receivingMode, extraUrls),
      },
    });
  };

  const onNameChange = (value: string) => {
    onChange?.({
      ...parameters,
      name: value,
    });

    setErrors({ ...errors, name: { isError: !value, isTouched: true } });
  };

  const onNameBlur = () => setErrors({ ...errors, name: { isError: errors.name.isError, isTouched: true } });

  const onBaseUrlChange = (value: string) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        source_type: IngestionSourceParametersType.Website,
        base_url: value,
      },
    });

    setErrors({ ...errors, base_url: { isError: !value, isTouched: true } });
  };

  const onBaseUrlBlur = () => setErrors({ ...errors, base_url: { isError: errors.base_url.isError, isTouched: true } });

  const onContentSelectorChange = (value: string) => {
    onChange?.({
      ...parameters,
      extraction: {
        file: parameters.extraction.file,
        html: {
          content_selector: value,
          filter_selector: parameters.extraction.html?.filter_selector,
        },
        text: parameters.extraction.text,
      },
    });
  };

  const onFilterSelectorChange = (value: string) => {
    onChange?.({
      ...parameters,
      extraction: {
        file: parameters.extraction.file,
        html: {
          content_selector: parameters.extraction.html?.content_selector,
          filter_selector: value,
        },
        text: parameters.extraction.text,
      },
    });
  };

  const onReceivingModeChange = (value: string | boolean) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        source_type: IngestionSourceParametersType.Website,
        receiving_mode: value as WebsiteSourceParametersReceivingMode,
      },
    });
  };

  const onExtraUrlsChange = (value: string) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        source_type: IngestionSourceParametersType.Website,
        extra_urls: value.split('\n'),
      },
    });

    validateExtraUrls(settings.receiving_mode, value.split('\n'));
  };

  const onExtraUrlsBlur = () => validateExtraUrls(settings.receiving_mode, settings.extra_urls);

  const onIncludedPathChange = (value: string) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        source_type: IngestionSourceParametersType.Website,
        included_paths: value,
      },
    });
  };

  const onExcludedPathChange = (value: string) => {
    onChange?.({
      ...parameters,
      settings: {
        ...settings,
        source_type: IngestionSourceParametersType.Website,
        excluded_paths: value,
      },
    });
  };

  const selectOptions = [
    {
      value: WebsiteSourceParametersReceivingMode.Crawler,
      label: t('Source settings: Website: Receiving mode: Crawler'),
    },
    {
      value: WebsiteSourceParametersReceivingMode.Manual,
      label: t('Source settings: Website: Receiving mode: Manual'),
    },
  ];

  const renderIncludedPath = () => {
    if (settings.receiving_mode !== WebsiteSourceParametersReceivingMode.Crawler) {
      return null;
    }

    return (
      <Col span={24}>
        <IbFormFieldLabel
          hint={t('Source settings: Website: Included path: Hint')}
          label={t('Source settings: Website: Included path: Title')}
        />
        <IbTextArea
          placeholder={INCLUDED_PATHS_PLACEHOLDER}
          rows={3}
          value={settings.included_paths}
          onChange={onIncludedPathChange}
        />
      </Col>
    );
  };

  const renderExcludedPath = () => {
    if (settings.receiving_mode !== WebsiteSourceParametersReceivingMode.Crawler) {
      return null;
    }

    return (
      <Col span={24}>
        <IbFormFieldLabel
          hint={t('Source settings: Website: Excluded path: Hint')}
          label={t('Source settings: Website: Excluded path: Title')}
        />
        <IbTextArea
          placeholder={EXCLUDED_PATHS_PLACEHOLDER}
          rows={3}
          value={settings.excluded_paths}
          onChange={onExcludedPathChange}
        />
        <IbTypography.Paragraph light type="descriptor">
          {t('Source settings: Website: Excluded path: Descriptor')}
        </IbTypography.Paragraph>
      </Col>
    );
  };

  const renderExtraUrls = () => {
    if (settings.receiving_mode !== WebsiteSourceParametersReceivingMode.Manual) {
      return null;
    }

    return (
      <Col span={24}>
        <IbTextArea
          placeholder={t('Source settings: Website: Extra URLs: Placeholder')}
          rows={6}
          status={errors.extra_urls.isTouched && errors.extra_urls.isError ? 'error' : 'default'}
          value={(settings.extra_urls || []).join('\n')}
          onBlur={onExtraUrlsBlur}
          onChange={onExtraUrlsChange}
        />
        {errors.extra_urls.isTouched && errors.extra_urls.isError && (
          <IbTypography.Paragraph error type="descriptor">
            {t('Source settings: Website: Extra URLs: Error')}
          </IbTypography.Paragraph>
        )}
        <IbTypography.Paragraph light type="descriptor">
          {t('Source settings: Website: Extra URLs: Descriptor')}
        </IbTypography.Paragraph>
      </Col>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <Row>
        <Col span={24}>
          <IbFormFieldLabel
            hint={t('Source settings: Common: Name: Hint')}
            label={t('Source settings: Website: Name: Title')}
          />
          <IbInput
            status={errors.name.isTouched && errors.name.isError ? 'error' : 'default'}
            value={parameters.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {errors.name.isTouched && errors.name.isError && (
            <IbTypography.Paragraph error type="descriptor">
              {t('Source settings: Website: Name: Error')}
            </IbTypography.Paragraph>
          )}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <IbFormFieldLabel
            hint={t('Source settings: Website: Base URL: Hint')}
            label={t('Source settings: Website: Base URL: Title')}
          />
          <IbInput
            prefix
            placeholder={t('Source settings: Website: Base URL: Placeholder')}
            status={errors.base_url.isTouched && errors.base_url.isError ? 'error' : 'default'}
            value={settings.base_url}
            onBlur={onBaseUrlBlur}
            onChange={onBaseUrlChange}
          />
          {errors.base_url.isTouched && errors.base_url.isError && (
            <IbTypography.Paragraph error type="descriptor">
              {t('Source settings: Website: Base URL: Error')}
            </IbTypography.Paragraph>
          )}
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <IbFormFieldLabel
            hint={t('Source settings: Website: Content selector: Hint')}
            label={t('Source settings: Website: Content selector: Title')}
          />
          <IbInput
            placeholder={t('Source settings: Website: Content selector: Placeholder')}
            value={parameters.extraction.html?.content_selector}
            onChange={onContentSelectorChange}
          />
        </Col>
        <Col span={12}>
          <IbFormFieldLabel
            hint={t('Source settings: Website: Filter selector: Hint')}
            label={t('Source settings: Website: Filter selector: Title')}
          />
          <IbInput
            placeholder={t('Source settings: Website: Filter selector: Placeholder')}
            value={parameters.extraction.html?.filter_selector}
            onChange={onFilterSelectorChange}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <IbFormFieldLabel
            hint={t('Source settings: Website: Receiving mode: Hint')}
            label={t('Source settings: Website: Receiving mode: Title')}
          />
          <Switcher options={selectOptions} value={settings.receiving_mode} onChange={onReceivingModeChange} />
        </Col>
        {renderIncludedPath()}
        {renderExcludedPath()}
        {renderExtraUrls()}
      </Row>
    </div>
  );
});

export default SourceSettingsWebsite;
